import {
  EPISODE_CONSOLIDATION_MIN_SPACING_MS,
} from "../../shared/recaps/consolidate.ts";
import type { RecapCoverageStatus, SuggestionCoverageStatus } from "../../shared/types.ts";

export type ChipTone = "live" | "paused" | "off" | "error" | "busy";

/** Right-aligned state pill on a function chip: short lowercase label
 *  (CSS uppercases it), dot tone, hover title carrying the longer phrasing. */
export interface ChipState {
  label: string;
  tone: ChipTone;
  title?: string;
}

/** Structural view of `status.consolidation` from the operator shell state —
 *  kept structural so unit tests don't import the React-flavored state module. */
export interface ConsolidationSnapshot {
  pendingEpisodeCount: number;
  timelinePhase: RecapCoverageStatus["phase"];
  previewPending: boolean;
  lastConsolidatedAt: number | null;
  inputSectionCount: number;
  spacingMs?: number;
}

export function transcriptChipState(args: {
  sttError: boolean;
  captureActive: boolean;
  capturePaused: boolean;
}): ChipState {
  if (args.sttError) return { label: "error", tone: "error", title: "Speech-to-text error" };
  if (!args.captureActive) return { label: "off", tone: "off", title: "Capture off" };
  if (args.capturePaused) return { label: "paused", tone: "paused", title: "Capture paused" };
  return { label: "live", tone: "live", title: "Listening" };
}

/** Run/idle state for the threshold-window chips (Suggestions passes the
 *  backend phase; Timeline has no phase split). Null = accruing, no pill. */
export function windowChipState(args: {
  busy: boolean;
  phase?: "generating" | "judging" | null;
  stale: boolean;
}): ChipState | null {
  if (args.busy) {
    return args.phase === "judging"
      ? { label: "judging", tone: "busy", title: "Judging candidates" }
      : { label: "generating", tone: "busy", title: "Generation running" };
  }
  if (args.stale) return { label: "waiting", tone: "off", title: "Waiting for new speech" };
  return null;
}

/** Durable Timeline state. Unlike `windowChipState`, this never reads the
 * suggestion transport checkpoint or guesses from client-held rows. */
export function recapCoverageChipState(
  coverage: Pick<RecapCoverageStatus, "phase">,
  accruingStale = false,
): ChipState {
  switch (coverage.phase) {
    case "blocked":
      return { label: "blocked", tone: "error", title: "Timeline coverage needs attention" };
    case "retrying":
      return { label: "retrying", tone: "paused", title: "Timeline generation will retry" };
    case "verifying":
      return { label: "verifying", tone: "busy", title: "Verifying corrected timeline coverage" };
    case "finalizing":
      return { label: "finalizing", tone: "busy", title: "Finalizing session coverage" };
    case "generating":
      return { label: "generating", tone: "busy", title: "Generating timeline coverage" };
    case "caught_up":
      return { label: "caught up", tone: "live", title: "Timeline coverage is caught up" };
    case "accruing":
      // Stale accrual reads as "waiting", not silence-counting — the same
      // semantics windowChipState carried before coverage owned the chip.
      return accruingStale
        ? { label: "waiting", tone: "off", title: "Waiting for new speech" }
        : { label: "accruing", tone: "off", title: "Accumulating the next content window" };
  }
}

/** Durable Suggestions state. This is projected from checkpoint leases and
 * outcomes, never from capture-runtime best-effort writes. */
export function suggestionCoverageChipState(
  coverage: Pick<SuggestionCoverageStatus, "phase" | "accruingStale">,
): ChipState {
  switch (coverage.phase) {
    case "blocked":
      return { label: "blocked", tone: "error", title: "Suggestion coverage needs attention" };
    case "retrying":
      return { label: "retrying", tone: "paused", title: "Suggestion generation will retry" };
    case "judging":
      return { label: "judging", tone: "busy", title: "Judging suggestion candidates" };
    case "generating":
      return { label: "generating", tone: "busy", title: "Generating suggestions" };
    case "caught_up":
      return { label: "caught up", tone: "live", title: "Suggestion coverage is caught up" };
    case "accruing":
      // Stale accrual reads as "waiting", not silence-counting — the same
      // semantics windowChipState carried before coverage owned the chip.
      return coverage.accruingStale
        ? { label: "waiting", tone: "off", title: "Waiting for new speech" }
        : { label: "accruing", tone: "off", title: "Accumulating the next content window" };
  }
}

/** Stats for a threshold-window chip. Null while a run executes (the run just
 *  consumed the window — the pill carries the state, matching the old
 *  "generating…" replacement). Stale drops the meaningless seconds figure. */
export function windowStatsLabel(args: {
  busy: boolean;
  stale: boolean;
  words: number;
  thresholdWords: number;
  seconds: number;
  thresholdSeconds: number;
  secondsFormat: "plain" | "mmss";
}): string | null {
  if (args.busy) return null;
  const words = `${args.words}/${args.thresholdWords}w`;
  if (args.stale) return words;
  const time = args.secondsFormat === "mmss"
    ? `${fmtMMSS(args.seconds)}/${fmtMMSS(args.thresholdSeconds)}`
    : `${args.seconds}/${args.thresholdSeconds}s`;
  return `${words} · ${time}`;
}

/** Compact progress for the semantic episode currently being accumulated.
 * Unlike Suggestions, Timeline does not expose user-editable thresholds. */
export function episodeStatsLabel(args: {
  busy: boolean;
  stale: boolean;
  words: number;
  seconds: number;
}): string | null {
  if (args.busy) return null;
  const words = `${args.words}w`;
  if (args.stale) return words;
  return `${words} · ${fmtMMSS(args.seconds)} live`;
}

/** mm:ss for a whole-second count (recap window progress / thresholds). */
export function fmtMMSS(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** Consolidated chip face driven by the authoritative episode frontier.
 * A blocked timeline always wins over an apparently empty backlog so the UI
 * can never claim that themes are caught up while coverage is stuck. */
export function describeConsolidatedChip(
  consolidation: ConsolidationSnapshot | null,
  elapsedSinceLastMs: number | null,
  generating: boolean,
  _captureState: "running" | "paused" | "stopped",
  _now: number,
): { stats: string; state: ChipState | null; alert: boolean } {
  if (!consolidation) return { stats: "—", state: null, alert: false };
  const pendingCount = consolidation.pendingEpisodeCount >= 100
    ? "99+"
    : String(consolidation.pendingEpisodeCount);
  const stats = `${pendingCount} pending`;
  if (consolidation.timelinePhase === "blocked") {
    return {
      stats,
      state: {
        label: "blocked",
        tone: "error",
        title: "Timeline is blocked; key themes cannot advance",
      },
      alert: true,
    };
  }
  if (generating) {
    return {
      stats: "",
      state: { label: "generating", tone: "busy", title: "Consolidation running" },
      alert: false,
    };
  }
  if (consolidation.previewPending) {
    return {
      stats,
      state: { label: "preview", tone: "paused", title: "Preview ready to publish" },
      alert: true,
    };
  }
  if (consolidation.pendingEpisodeCount === 0) {
    if (
      consolidation.lastConsolidatedAt === null &&
      consolidation.inputSectionCount === 0
    ) {
      return {
        stats,
        state: { label: "waiting", tone: "off", title: "Waiting for substantive discussion" },
        alert: false,
      };
    }
    return {
      stats,
      state: { label: "caught up", tone: "live", title: "Consolidated summary is caught up" },
      alert: false,
    };
  }
  if (consolidation.lastConsolidatedAt == null) {
    return { stats, state: themesState(0), alert: false };
  }
  const spacing = consolidation.spacingMs ?? EPISODE_CONSOLIDATION_MIN_SPACING_MS;
  return {
    stats,
    state: themesState(Math.max(0, spacing - (elapsedSinceLastMs ?? 0))),
    alert: false,
  };
}

function themesState(remainingMs: number): ChipState {
  const remainingSeconds = Math.ceil(Math.max(0, remainingMs) / 1000);
  const label = remainingSeconds <= 0
    ? "themes now"
    : remainingSeconds <= 120
      ? `themes ~${remainingSeconds}s`
      : `themes ~${Math.ceil(remainingSeconds / 60)}m`;
  return { label, tone: "off", title: "Next key themes publication" };
}
